/**
 * zona.js — shared vanilla-JS interactivity modules for the Zona theme.
 *
 * Modules: Accordion, Reveal, Carousel, Sticky bar (legacy, unused on /),
 * Sticky2 (two-sentinel), Buy box, Hero video, Day counter, Disclosure.
 *
 * Source of truth: source-replit-current/src/pages/ZonaSandbox.tsx (the
 * live homepage, "ZonaSandbox"). Do NOT cite source-replit/ (legacy
 * ZonaLanding sibling page) — every comment below cites ZonaSandbox.tsx.
 *
 * Source parity notes (source-replit-current/src/pages/ZonaSandbox.tsx):
 *   - Accordion:    PdpAccordions 965-1126 (toggle + scrollIntoView 969-978).
 *   - Reveal:       WhatMaturingMeans 367-376 (0.15), EggComparison90 528-537
 *                   (0.25), SandboxZonaVsRest 1258-1272 (0.12 + reduced-motion
 *                   check). Per-instance threshold via data-reveal-threshold.
 *   - Carousel:     ProductImageGallery 294-362 (swipe 307-313, thumbs 339-359).
 *   - Sticky (legacy, one-sentinel + localStorage): NOT present anywhere in
 *     ZonaSandbox.tsx. Kept only for backward compatibility with any
 *     out-of-scope page that may still reference [data-zona-sticky]; UNUSED
 *     ON / — the homepage uses Sticky2 below instead.
 *   - Sticky2:      ZonaSandbox() root component, logic 1606-1624 (two refs,
 *                   plain scroll listener + getBoundingClientRect, no
 *                   IntersectionObserver, no localStorage), render 3255-3287
 *                   (transform/pointer-events, "transform 0.35s
 *                   cubic-bezier(0.4,0,0.2,1)").
 *   - Buy box:      pricingPlan state 1595 (source default "subscribe");
 *                   card sets + hidden selling_plan input at both PDP call
 *                   sites (S7's zona-pdp.liquid renders pdp1/pdp2 from the
 *                   same snippet). data-buybox-default lets Liquid assert
 *                   the "subscription" default per INV §7 without the module
 *                   guessing at a source-string ("subscribe") that doesn't
 *                   match this module's own one-time|subscription enum.
 *   - Hero video:   ZonaSandbox() root component 1626-1658 (idle-gate
 *                   1630-1649, playbackRate 1650-1658). Own local gate, no
 *                   ZonaDefer, no interaction trigger.
 *   - Day counter:  CAL_COLS/CAL_TOTAL consts 645-646, EggIntroSection
 *                   648-812 (isMobile resize effect 654-658, tick effect
 *                   660-667, calDay/calProgress/calDone/eggSize 669-672).
 *   - Disclosure:   SandboxTheGap 1428-1590 (openFull 1434-1441: one-way
 *                   reveal + rAF-gated fade-in + reduced-motion-gated
 *                   scrollIntoView).
 *
 * ZonaDefer (the shared first-interaction-or-idle scheduler used by the
 * ZonaLanding-era port) has been DELETED in this task. Nothing in
 * ZonaSandbox.tsx uses a shared "idle OR first interaction" gate — the hero
 * video (1636-1639) and Mouseflow (S11, source 504-517) each use their OWN
 * idle-or-timeout gate with no interaction trigger, because a first-scroll
 * trigger would start bandwidth-competing work (video download / third-party
 * script fetch) exactly when the browser is still painting the LCP.
 */

/* ============================================================
   Accordion — [data-zona-accordion] > [data-acc-trigger] / [data-acc-panel]
   One-open-at-a-time (matches PdpAccordions' single `open` string state,
   ZonaSandbox.tsx 966-978).
   Panel height animates via max-height; timing (0.2s ease) is this module's
   own choice — ZonaSandbox.tsx animates PdpAccordions/SandboxDoseRow panels
   via plain conditional render (no CSS transition on the panel itself), so
   there is no single literal transition value to port here; 0.2s ease
   matches the "+" rotation timing used at both accordion sites (923, 999)
   for a consistent open/close feel.
   Icon rotation itself is left to section CSS via the
   [data-acc-trigger][aria-expanded="true"] selector — this module only
   guarantees aria-expanded + max-height (+ optional scrollIntoView below).

   data-acc-scroll (EXTEND): optional attribute on [data-acc-trigger] — when
   present AND the trigger is being opened, scrollIntoView's the trigger
   after the browser paints the newly-opened panel. Matches PdpAccordions'
   toggle() (969-978): on open, requestAnimationFrame then
   rowRefs.current[which]?.scrollIntoView({behavior: reduce?"auto":"smooth",
   block:"start"}), reduced-motion checked live at click time (974), not
   cached. Source scrolls the ref'd row wrapper (trigger + panel together);
   scrolling the trigger itself lands on the same top edge since the trigger
   is always the row's first child, so no extra wrapper element is required
   of the markup.
   ============================================================ */
(function () {
  var TRANSITION = "max-height 0.2s ease";

  function closePanel(panel) {
    panel.style.maxHeight = "0px";
  }

  function openPanel(panel) {
    panel.style.maxHeight = panel.scrollHeight + "px";
  }

  function initAccordion(container) {
    var triggers = container.querySelectorAll("[data-acc-trigger]");
    var panels = container.querySelectorAll("[data-acc-panel]");

    Array.prototype.forEach.call(triggers, function (trigger, i) {
      var panel = panels[i];
      if (!panel) return;

      panel.style.overflow = "hidden";
      panel.style.transition = TRANSITION;
      trigger.setAttribute("aria-expanded", "false");
      closePanel(panel);

      trigger.addEventListener("click", function () {
        var isOpen = trigger.getAttribute("aria-expanded") === "true";
        var nextOpen = !isOpen;

        // Close every item in this accordion (single-open-at-a-time).
        Array.prototype.forEach.call(triggers, function (t, j) {
          if (t === trigger) return;
          t.setAttribute("aria-expanded", "false");
          if (panels[j]) closePanel(panels[j]);
        });

        trigger.setAttribute("aria-expanded", nextOpen ? "true" : "false");
        if (nextOpen) {
          openPanel(panel);
          if (trigger.hasAttribute("data-acc-scroll")) {
            requestAnimationFrame(function () {
              var reduce = !!(
                window.matchMedia &&
                window.matchMedia("(prefers-reduced-motion: reduce)").matches
              );
              trigger.scrollIntoView({
                behavior: reduce ? "auto" : "smooth",
                block: "start",
              });
            });
          }
        } else {
          closePanel(panel);
        }
      });
    });
  }

  function init() {
    var containers = document.querySelectorAll("[data-zona-accordion]");
    Array.prototype.forEach.call(containers, initAccordion);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Reveal — [data-reveal] (+ [data-reveal-threshold], [data-reveal-delay])
   IntersectionObserver adds .is-visible after the optional delay (ms);
   unobserves on first intersect regardless of the delay's completion
   (matches WhatMaturingMeans/EggComparison90/SandboxZonaVsRest: obs.disconnect()
   runs synchronously inside the intersecting branch — ZonaSandbox.tsx
   371, 532, 1262).

   data-reveal-threshold (EXTEND): ZonaSandbox.tsx uses three different
   thresholds for the same reveal-on-scroll pattern — WhatMaturingMeans 0.15
   (372), EggComparison90 0.25 (533), SandboxZonaVsRest 0.12 (1263).
   IntersectionObserver's threshold is fixed at construction, so a single
   observer cannot serve elements that need different thresholds — this
   module groups [data-reveal] elements by their (parsed) threshold value
   and creates one observer per distinct group. Elements with no
   data-reveal-threshold attribute fall into the DEFAULT_THRESHOLD (0.15)
   group. Per-row stagger (SandboxZonaVsRest's `120 + i*45`ms, 1264-ish)
   needs no new JS — it's just a bigger data-reveal-delay computed in Liquid
   via forloop.index0, handled by the existing delay logic below.
   ============================================================ */
(function () {
  var DEFAULT_THRESHOLD = 0.15;

  function reveal(el) {
    var delay = parseInt(el.getAttribute("data-reveal-delay"), 10) || 0;
    if (delay > 0) {
      setTimeout(function () {
        el.classList.add("is-visible");
      }, delay);
    } else {
      el.classList.add("is-visible");
    }
  }

  function groupByThreshold(els) {
    var groups = {};
    Array.prototype.forEach.call(els, function (el) {
      var raw = el.getAttribute("data-reveal-threshold");
      var t = raw !== null ? parseFloat(raw) : NaN;
      if (isNaN(t)) t = DEFAULT_THRESHOLD;
      var key = String(t);
      if (!groups[key]) groups[key] = { threshold: t, els: [] };
      groups[key].els.push(el);
    });
    return groups;
  }

  function init() {
    var els = document.querySelectorAll("[data-reveal]");
    if (!("IntersectionObserver" in window)) {
      // No IO support: reveal immediately rather than leaving content hidden forever.
      Array.prototype.forEach.call(els, reveal);
      return;
    }

    var groups = groupByThreshold(els);
    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              reveal(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: group.threshold }
      );
      group.els.forEach(function (el) {
        observer.observe(el);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Carousel — [data-zona-carousel]
   Sub-attributes (chosen here; not individually named in the brief, which
   only fixes the container hook — documented as the frozen contract for
   section tasks to build markup against):
     [data-carousel-frame]  wrapper that receives touch handlers (required)
     [data-carousel-slide]  one per slide, in DOM order (required, 1+)
     [data-carousel-prev]   prev arrow button (optional)
     [data-carousel-next]   next arrow button (optional)
     [data-carousel-dots]   dot container (optional)
     [data-carousel-dot]    one per slide inside data-carousel-dots, in DOM
                             order, count MUST match slide count (optional)
   Active slide/dot marked with the `.is-active` class; opacity/timing
   ("opacity 0.35s ease") set inline here to match ProductImageGallery's
   image crossfade exactly (ZonaSandbox.tsx 328).
   Swipe threshold: |deltaX| <= 40 is ignored (ZonaSandbox.tsx 310,
   `Math.abs(dx) > 40`).

   Thumbnails (EXTEND — no new JS hook): ProductImageGallery's thumbnail
   strip (339-359) is functionally identical to [data-carousel-dot] — a
   button per slide that sets the active index on click. The gallery styles
   its dots as 54x54 thumbnail <img>s (section CSS) instead of small round
   dots; no new data attribute or JS behavior is needed for this, per the
   brief ("NO new hook").
   ============================================================ */
(function () {
  var CROSSFADE = "opacity 0.35s ease";
  var SWIPE_THRESHOLD = 40;

  function initCarousel(root) {
    var frame = root.querySelector("[data-carousel-frame]");
    var slides = root.querySelectorAll("[data-carousel-slide]");
    var prevBtn = root.querySelector("[data-carousel-prev]");
    var nextBtn = root.querySelector("[data-carousel-next]");
    var dots = root.querySelectorAll("[data-carousel-dot]");
    var count = slides.length;
    if (!count) return;

    var index = 0;

    Array.prototype.forEach.call(slides, function (slide) {
      slide.style.transition = CROSSFADE;
    });

    function render() {
      Array.prototype.forEach.call(slides, function (slide, i) {
        var active = i === index;
        slide.classList.toggle("is-active", active);
        slide.style.opacity = active ? "1" : "0";
      });
      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.classList.toggle("is-active", i === index);
      });
    }

    function goTo(i) {
      index = ((i % count) + count) % count;
      render();
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        goTo(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        goTo(index + 1);
      });
    }
    Array.prototype.forEach.call(dots, function (dot, i) {
      dot.addEventListener("click", function () {
        goTo(i);
      });
    });

    if (frame) {
      var touchX = null;
      frame.addEventListener(
        "touchstart",
        function (e) {
          touchX = e.touches[0].clientX;
        },
        { passive: true }
      );
      frame.addEventListener("touchend", function (e) {
        if (touchX === null) return;
        var delta = e.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(delta) <= SWIPE_THRESHOLD) return;
        goTo(index + (delta < 0 ? 1 : -1));
      });
    }

    render();
  }

  function init() {
    var roots = document.querySelectorAll("[data-zona-carousel]");
    Array.prototype.forEach.call(roots, initCarousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Sticky bar (LEGACY, one-sentinel + localStorage) — UNUSED ON /
   [data-zona-sticky] + [data-sticky-sentinel]

   This module does not correspond to anything in ZonaSandbox.tsx — the
   live homepage's sticky buy bar uses the two-sentinel Sticky2 module
   below (source 1606-1624 + 3255-3287). This one-sentinel/localStorage
   variant was built against the wrong sibling page in an earlier task and
   is kept, unmodified, only so nothing that might still reference
   [data-zona-sticky] elsewhere breaks. No section task should emit
   [data-zona-sticky] markup for the homepage; if this module is ever
   found to be truly dead across the whole theme, it can be deleted.
   ============================================================ */
(function () {
  var STORAGE_KEY = "zona_sticky_seen";

  function getSeen() {
    try {
      return !!localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return false;
    }
  }

  function setSeen() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore (private mode / storage disabled) */
    }
  }

  function initSticky(bar) {
    var sentinel = document.querySelector("[data-sticky-sentinel]");
    if (!sentinel || !("IntersectionObserver" in window)) return;

    bar.style.transition = "opacity 0.3s ease";

    var seen = getSeen();

    function setVisible(visible) {
      bar.classList.toggle("is-visible", visible);
      bar.style.opacity = visible ? "1" : "0";
      bar.style.pointerEvents = visible ? "auto" : "none";
    }

    setVisible(false);

    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        var inView = entry.isIntersecting;
        if (!inView && entry.boundingClientRect.bottom <= 0) {
          seen = true;
          setSeen();
        }
        setVisible(seen && !inView);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
  }

  function init() {
    var bars = document.querySelectorAll("[data-zona-sticky]");
    Array.prototype.forEach.call(bars, initSticky);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Sticky2 — [data-zona-sticky2] + [data-sticky-a] / [data-sticky-b]
   Ports ZonaSandbox()'s actual sticky-buy-bar logic verbatim (1606-1624):
   plain `scroll` listener (passive) that reads getBoundingClientRect() on
   two section refs every scroll tick — NOT an IntersectionObserver, and NO
   localStorage. Visible when sentinel A has been scrolled past
   (r1.bottom <= 0) AND sentinel B is not currently in view
   (!(r2.bottom > 0 && r2.top < innerHeight)). Runs once immediately on
   init (source line 1622, `onScroll()` called synchronously after the
   listener is attached) and re-evaluates on every scroll — the bar can
   show, hide, and RE-SHOW as many times as the two sentinels' visibility
   changes (e.g. hidden while sentinel B, the second PDP, is in view; shown
   again once the user scrolls past it), matching source's plain boolean
   state with no "seen" flag.

   Visual transition ("transform 0.35s cubic-bezier(0.4,0,0.2,1)" +
   pointer-events, ZonaSandbox.tsx 3262-3264) is set inline here to match
   the source's literal value. The pre-JS/no-JS default appearance
   (e.g. a static `opacity:0` fallback so the fixed bar isn't visible
   before this script runs) is the owning section's CSS responsibility, not
   this module's — see the S9a section task, which places [data-sticky-a]
   on the first PDP section root and [data-sticky-b] on the second, exactly
   matching source's pdp1Ref/pdp2Ref (2537, 2744).
   ============================================================ */
(function () {
  var TRANSITION = "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)";

  function initSticky2(bar) {
    var a = document.querySelector("[data-sticky-a]");
    var b = document.querySelector("[data-sticky-b]");
    if (!a || !b) return;

    bar.style.transition = TRANSITION;

    function setVisible(visible) {
      bar.classList.toggle("is-visible", visible);
      bar.style.transform = visible ? "translateY(0)" : "translateY(100%)";
      bar.style.pointerEvents = visible ? "auto" : "none";
    }

    function onScroll() {
      var rA = a.getBoundingClientRect();
      var rB = b.getBoundingClientRect();
      var passedA = rA.bottom <= 0;
      var bInView = rB.bottom > 0 && rB.top < window.innerHeight;
      setVisible(passedA && !bInView);
    }

    setVisible(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  function init() {
    var bars = document.querySelectorAll("[data-zona-sticky2]");
    Array.prototype.forEach.call(bars, initSticky2);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Buy box — [data-zona-buybox] (form) > [data-purchase-card="one-time|subscription"]
   Shared module-level `purchaseType` state (ZonaSandbox.tsx pricingPlan,
   line 1595) — selecting a card in ANY buy box instance updates ALL
   instances' card styling and hidden selling_plan input. Both PDP sections
   (S7's zona-pdp.liquid rendered at pdp1/pdp2) share this one module-scope
   state, matching the source's single pricingPlan state driving both call
   sites.
   Selling-plan input lookup: [data-selling-plan] (falls back to
   input[name="selling_plan"]) carrying [data-plan-id] with the plan id.
   subscription -> input.value = data-plan-id, disabled = false
   one-time     -> input.value = '',            disabled = true

   data-buybox-default (EXTEND): source's default is the string "subscribe"
   (line 1595) — a different enum than this module's own "one-time" |
   "subscription" values, so Liquid asserts the intended default explicitly
   via data-buybox-default="subscription" rather than this module trying to
   map source's string. Read from the FIRST [data-zona-buybox] element
   encountered during init(), and assigned to module-scope `purchaseType`
   BEFORE any applyState() call runs (i.e. before the per-instance init loop
   below starts) — both PDP snippet instances must render identical values,
   which this ordering guarantees since applyState() only ever runs inside
   initBuyBox(), never before init()'s own attribute read.
   ============================================================ */
(function () {
  var purchaseType = "one-time"; // fallback if no [data-zona-buybox] carries data-buybox-default
  var instances = []; // { root, cards, input }

  function applyState(instance) {
    var input = instance.input;
    Array.prototype.forEach.call(instance.cards, function (card) {
      var isSelected = card.getAttribute("data-purchase-card") === purchaseType;
      card.classList.toggle("is-selected", isSelected);
      card.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });

    if (!input) return;
    if (purchaseType === "subscription") {
      input.value = input.getAttribute("data-plan-id") || "";
      input.disabled = false;
    } else {
      input.value = "";
      input.disabled = true;
    }
  }

  function applyAll() {
    instances.forEach(applyState);
  }

  function selectType(type) {
    if (type !== "one-time" && type !== "subscription") return;
    purchaseType = type;
    applyAll();
  }

  function initBuyBox(root) {
    var cards = root.querySelectorAll("[data-purchase-card]");
    var input =
      root.querySelector("[data-selling-plan]") ||
      root.querySelector('input[name="selling_plan"]');

    var instance = { root: root, cards: cards, input: input };
    instances.push(instance);

    Array.prototype.forEach.call(cards, function (card) {
      card.addEventListener("click", function () {
        selectType(card.getAttribute("data-purchase-card"));
      });
    });

    applyState(instance);
  }

  function init() {
    var roots = document.querySelectorAll("[data-zona-buybox]");

    if (roots.length) {
      var def = roots[0].getAttribute("data-buybox-default");
      if (def === "one-time" || def === "subscription") {
        purchaseType = def;
      }
    }

    Array.prototype.forEach.call(roots, initBuyBox);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Hero video — [data-zona-herovideo]
   Ports ZonaSandbox()'s heroVideoReady gate verbatim (1630-1649): fires
   once, on whichever comes first — requestIdleCallback with a 3000ms
   timeout, or (no requestIdleCallback support) a flat 1500ms setTimeout.
   Deliberately its OWN locally-scoped gate, NOT ZonaDefer (deleted in this
   task) — no interaction trigger, since a first-scroll trigger would start
   the video download while the browser may still be painting the LCP
   poster image (comment at ZonaSandbox.tsx 1626-1628).

   On fire: playbackRate = 0.8 is set immediately AND re-asserted on the
   `loadedmetadata` event (1650-1658, source does both for robustness since
   some browsers ignore playbackRate set before metadata is available).

   Deferred network fetch mechanism (source's actual mechanism is React
   conditionally mounting the <video> element only once heroVideoReady is
   true, so the browser never requests the file until then — not
   individually specified in the S2 brief beyond "its own gate ... plus
   playbackRate on loadedmetadata"). This module's equivalent for static
   markup, frozen as the contract for S3: the <video data-zona-herovideo>
   (or its <source> children) carries the real URL in data-src instead of
   src; on gate-fire this module copies data-src -> src, calls .load(), and
   attempts .play() (the element's own autoplay/muted/playsinline
   attributes, set by S3's markup, make the play() call a no-op safety net
   on browsers that already autoplay it, and a graceful no-throw on
   browsers that block it — the poster stays visible either way).
   ============================================================ */
(function () {
  function activate(video) {
    var sources = video.querySelectorAll("source[data-src]");
    if (sources.length) {
      Array.prototype.forEach.call(sources, function (source) {
        source.src = source.getAttribute("data-src");
      });
      video.load();
    } else if (video.hasAttribute("data-src")) {
      video.src = video.getAttribute("data-src");
      video.load();
    }

    video.playbackRate = 0.8;
    video.addEventListener("loadedmetadata", function () {
      video.playbackRate = 0.8;
    });

    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(function () {
        /* autoplay blocked by the browser; poster image remains visible */
      });
    }
  }

  function initHeroVideo(video) {
    function ready() {
      activate(video);
    }

    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(ready, { timeout: 3000 });
    } else {
      setTimeout(ready, 1500);
    }
  }

  function init() {
    var videos = document.querySelectorAll("[data-zona-herovideo]");
    Array.prototype.forEach.call(videos, initHeroVideo);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Day counter — [data-zona-daycounter]
   Ports EggIntroSection verbatim (ZonaSandbox.tsx 645-812):
     CAL_COLS = 10, CAL_TOTAL = CAL_COLS * 9 = 90 (645-646)
     tick effect (660-667): setInterval(..., 65); t = t >= CAL_TOTAL+16
       (106) ? 0 : t+1 — i.e. tick counts 1..106 then resets to 0 (a ~1s
       "hold" at the full 90-cell grid before the animation restarts).
     calDay = min(tick, CAL_TOTAL); calProgress = calDay/CAL_TOTAL;
       calDone = calDay >= CAL_TOTAL (669-671).
     eggSize = isMobile ? 20+calProgress*20 : 30+calProgress*30 (672).
     isMobile resize effect (654-658): window.innerWidth < 640, updated on
       resize (separate effect from the tick interval, matching source).

   Sub-attributes (chosen here; frozen contract for the S5 section task
   that builds zona-egg-intro's markup):
     [data-daycounter-cell]      one per calendar cell, CAL_TOTAL (90)
                                 required, in DOM order = index i (matches
                                 source's Array.from({length: CAL_TOTAL})
                                 .map((_, i) => ...), 715/760). This module
                                 toggles `.is-filled` (i < calDay) and
                                 `.is-today` (i === calDay-1 && !calDone);
                                 fill/border colors and the today-cell
                                 transform scale (1.25 mobile / 1.18 desktop,
                                 724/769) are section CSS driven off those
                                 classes — this module does not set cell
                                 colors or transforms inline. (The mobile/
                                 desktop scale split is a media-query rule in
                                 zona-egg-intro.liquid; see the root note
                                 below.)
     [data-daycounter-egg]       the growing circle. This module sets
                                 width/height (eggSize) and box-shadow
                                 inline every tick, since both are
                                 continuously calProgress-driven (not a
                                 static breakpoint difference expressible in
                                 CSS alone): box-shadow radius/opacity
                                 mirrors 742 (mobile, 6+calProgress*18px /
                                 rgba(13,77,71,0.15+calProgress*0.3)) and
                                 779 (desktop, 8+calProgress*26px, same
                                 color formula) — 13,77,71 = C.tealDeep
                                 (#0d4d47, the same dark-teal from S1's
                                 critical CSS).
     [data-daycounter-count]     text node set to calDay every tick
                                 (747/783).
     [data-daycounter-daylabel]  optional; gets `.is-done` toggled when
                                 calDone, matching the "DAY 90" label's
                                 color transition to C.tealDeep (712/757).
                                 Mobile vs. desktop label TEXT ("DAY 90" vs.
                                 "DAY 90 · OVULATION") is left to section
                                 markup/CSS (a "· OVULATION" suffix span
                                 shown/hidden by media query), not this
                                 module.

   Root gets `.is-mobile` toggled to match the isMobile resize effect. The
   ACTUAL contract as consumed by zona-egg-intro.liquid (S5): this module
   owns `.is-mobile` plus all per-tick inline styles (egg width/height/
   box-shadow, count text, cell classes); the section deliberately does NOT
   key its breakpoint layout (grid-template-columns 18 vs 10 cols, gap,
   label text, today-scale) off `.is-mobile` — it uses a plain
   `@media (min-width: 640px)` block at the same literal 640px boundary as
   this module's isMobile check. Intentional FOUC-avoidance exception: a
   class-driven layout would render the wrong (no-class default) branch
   until this deferred script runs; the media query paints the correct
   layout from static CSS immediately. The 640px boundary is therefore
   duplicated in two places by design — if it ever changes, update BOTH
   this module and zona-egg-intro.liquid's media query. `.is-mobile` is
   still toggled here for any future consumer that needs the JS-tracked
   boundary.
   ============================================================ */
(function () {
  var CAL_COLS = 10;
  var CAL_TOTAL = CAL_COLS * 9; // 90
  var CAL_MAX_TICK = CAL_TOTAL + 16; // 106
  var TICK_MS = 65;

  function initDayCounter(root) {
    var cells = root.querySelectorAll("[data-daycounter-cell]");
    var egg = root.querySelector("[data-daycounter-egg]");
    var countEl = root.querySelector("[data-daycounter-count]");
    var dayLabel = root.querySelector("[data-daycounter-daylabel]");

    var isMobile = window.innerWidth < 640;

    function applyMobileClass() {
      root.classList.toggle("is-mobile", isMobile);
    }
    applyMobileClass();

    window.addEventListener("resize", function () {
      isMobile = window.innerWidth < 640;
      applyMobileClass();
    });

    function render(tick) {
      var calDay = Math.min(tick, CAL_TOTAL);
      var calProgress = calDay / CAL_TOTAL;
      var calDone = calDay >= CAL_TOTAL;

      Array.prototype.forEach.call(cells, function (cell, i) {
        var filled = i < calDay;
        var isToday = i === calDay - 1 && !calDone;
        cell.classList.toggle("is-filled", filled);
        cell.classList.toggle("is-today", isToday);
      });

      if (egg) {
        var eggSize = isMobile
          ? 20 + calProgress * 20
          : 30 + calProgress * 30;
        var blur = isMobile ? 6 + calProgress * 18 : 8 + calProgress * 26;
        var alpha = 0.15 + calProgress * 0.3;
        egg.style.width = eggSize + "px";
        egg.style.height = eggSize + "px";
        egg.style.boxShadow =
          "0 0 " + blur + "px rgba(13,77,71," + alpha + ")";
      }

      if (countEl) {
        countEl.textContent = String(calDay);
      }

      if (dayLabel) {
        dayLabel.classList.toggle("is-done", calDone);
      }
    }

    var tick = 0;
    render(tick);
    setInterval(function () {
      tick = tick >= CAL_MAX_TICK ? 0 : tick + 1;
      render(tick);
    }, TICK_MS);
  }

  function init() {
    var roots = document.querySelectorAll("[data-zona-daycounter]");
    Array.prototype.forEach.call(roots, initDayCounter);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ============================================================
   Disclosure — [data-zona-disclosure] > [data-disc-trigger] / [data-disc-panel]
   Ports SandboxTheGap's openFull() verbatim (ZonaSandbox.tsx 1428-1441):
   one-way click reveal, no re-collapse. On click: the trigger button
   disappears entirely (source's `{!showFull && (<button>...)}`, 1506-1523)
   and the panel is revealed (`{showFull && (<div ref={fullRef} ...)}`,
   1527), rendered first at opacity:0/translateY(16px), then next frame
   flipped to opacity:1/translateY(0) — the two-step paint is what makes
   the "opacity 0.6s ease, transform 0.6s ease" transition (1531-1532)
   actually animate rather than jump straight to visible. scrollIntoView is
   gated by prefers-reduced-motion, checked live at click time (1436), same
   as PdpAccordions' toggle — NOT gated for the fade-in transition itself,
   matching source exactly.
   { once: true } on the click listener is redundant with the trigger being
   hidden (so it can't be clicked again) but documents the one-way contract
   explicitly.
   ============================================================ */
(function () {
  function initDisclosure(root) {
    var trigger = root.querySelector("[data-disc-trigger]");
    var panel = root.querySelector("[data-disc-panel]");
    if (!trigger || !panel) return;

    panel.hidden = true;
    panel.style.transition = "opacity 0.6s ease, transform 0.6s ease";
    panel.style.opacity = "0";
    panel.style.transform = "translateY(16px)";

    trigger.addEventListener(
      "click",
      function () {
        trigger.hidden = true;
        panel.hidden = false;

        // Force a layout so the browser registers the "hidden" starting
        // values above before the next frame flips to the visible ones —
        // otherwise the two style writes can coalesce into one paint and
        // the transition never runs.
        void panel.offsetHeight;

        requestAnimationFrame(function () {
          panel.style.opacity = "1";
          panel.style.transform = "translateY(0)";

          var reduce = !!(
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
          );
          panel.scrollIntoView({
            behavior: reduce ? "auto" : "smooth",
            block: "start",
          });
        });
      },
      { once: true }
    );
  }

  function init() {
    var roots = document.querySelectorAll("[data-zona-disclosure]");
    Array.prototype.forEach.call(roots, initDisclosure);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
